/**
 * Authentication Service - Handles all auth-related API calls
 * 
 * Features:
 * - Login with email/password
 * - Logout
 * - Get current user
 * - JWT token management
 */

import { post, get, del, setToken, removeToken, ApiResponse } from './api';
import { User, GoogleAuthResult, ProfileCompletionData } from '../types/Auth.types';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    doctorId?: string;
    patientId?: string;
  };
}

interface GetMeResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  doctor?: {
    id: string;
    specialization: string;
    licenseNumber: string;
    phoneNumber: string;
    status: string;
  };
  patient?: {
    id: string;
    dateOfBirth: string;
    gender: string;
    bloodType: string;
    phoneNumber: string;
  };
}

/**
 * Login with email and password
 */
export const login = async (credentials: LoginRequest): Promise<User> => {
  const response = await post<ApiResponse<LoginResponse>>(
    '/auth/login',
    credentials,
    false // Don't include auth header for login
  );

  // Store token
  setToken(response.data.token);

  // Transform backend user to frontend User type
  const user: User = {
    id: response.data.user.id,
    email: response.data.user.email,
    name: response.data.user.name,
    role: response.data.user.role,
    doctorId: response.data.user.doctorId,
    patientId: response.data.user.patientId,
  };

  // Store user in localStorage
  localStorage.setItem('docx_user', JSON.stringify(user));

  return user;
};

/**
 * Logout current user
 */
export const logout = async (): Promise<void> => {
  try {
    // Call backend logout endpoint
    await post('/auth/logout', {});
  } catch (error) {
    // Continue with logout even if API call fails
    console.error('Logout API call failed:', error);
  } finally {
    // Always clean up local storage
    removeToken();
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await get<ApiResponse<GetMeResponse>>('/auth/me');

  // Transform backend user to frontend User type
  const user: User = {
    id: response.data.id,
    email: response.data.email,
    name: response.data.name,
    role: response.data.role,
    doctorId: response.data.doctor?.id,
    patientId: response.data.patient?.id,
  };

  // Update user in localStorage
  localStorage.setItem('docx_user', JSON.stringify(user));

  return user;
};

/**
 * Check if user has a valid token
 */
export const hasValidToken = (): boolean => {
  const token = localStorage.getItem('docx_token');
  return !!token;
};

interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  gender: string;
  phone?: string;
  role: 'DOCTOR' | 'PATIENT';
  specialization?: string;
  licenseNumber?: string;
}

/**
 * Register a new doctor
 */
export const register = async (credentials: RegisterRequest): Promise<User> => {
  const response = await post<ApiResponse<LoginResponse>>(
    '/auth/register',
    credentials,
    false
  );

  setToken(response.data.token);

  const user: User = {
    id: response.data.user.id,
    email: response.data.user.email,
    name: response.data.user.name,
    role: response.data.user.role,
    doctorId: response.data.user.doctorId,
    patientId: response.data.user.patientId,
  };

  localStorage.setItem('docx_user', JSON.stringify(user));
  return user;
};

interface GoogleAuthRequest {
  credential: string;
  profileData?: {
    role?: 'DOCTOR' | 'PATIENT';
    specialization?: string;
    licenseNumber?: string;
    gender?: string;
    phone?: string;
  };
}

/**
 * Authenticate with Google
 */
export const googleAuth = async (request: GoogleAuthRequest): Promise<GoogleAuthResult> => {
  const response = await post<ApiResponse<GoogleAuthResult>>(
    '/auth/google',
    request,
    false
  );

  if (response.data.token) {
    setToken(response.data.token);
    localStorage.setItem('docx_user', JSON.stringify(response.data.user));
  }

  return response.data;
};

/**
 * Complete profile after Google OAuth (new users)
 */
export const completeProfile = async (data: ProfileCompletionData): Promise<User> => {
  const response = await post<ApiResponse<LoginResponse>>(
    '/auth/complete-profile',
    data,
    false
  );

  setToken(response.data.token);

  const user: User = {
    id: response.data.user.id,
    email: response.data.user.email,
    name: response.data.user.name,
    role: response.data.user.role,
    doctorId: response.data.user.doctorId,
    patientId: response.data.user.patientId,
  };

  localStorage.setItem('docx_user', JSON.stringify(user));
  return user;
};

/**
 * Session types
 */
export interface Session {
  id: string;
  deviceName: string;
  deviceType: string;
  browser: string;
  os: string;
  ipAddress: string;
  lastActiveAt: string;
  createdAt: string;
  isCurrent: boolean;
}

/**
 * Get active sessions for the current user
 */
export const getSessions = async (): Promise<Session[]> => {
  const response = await get<ApiResponse<Session[]>>('/auth/sessions');
  return response.data;
};

/**
 * Revoke a specific session
 */
export const revokeSession = async (sessionId: string): Promise<void> => {
  await del<ApiResponse<{ message: string }>>(`/auth/sessions/${sessionId}`);
};

/**
 * Revoke all sessions except current
 */
export const revokeAllSessions = async (): Promise<void> => {
  await post<ApiResponse<{ message: string }>>('/auth/sessions/revoke-all', {});
};

const authService = {
  login,
  logout,
  getCurrentUser,
  hasValidToken,
  register,
  googleAuth,
  completeProfile,
  getSessions,
  revokeSession,
  revokeAllSessions,
};

export default authService;
